import { offlineStorageService } from './offlineStorage'
import { apiService } from './api'

interface SyncStatus {
  isOnline: boolean
  lastSyncTime: Date | null
  pendingSync: number
  isSyncing: boolean
}

class OfflineSyncService {
  private syncStatus: SyncStatus = {
    isOnline: navigator.onLine,
    lastSyncTime: null,
    pendingSync: 0,
    isSyncing: false
  }

  private listeners: Array<(status: SyncStatus) => void> = []

  constructor() {
    this.setupNetworkListeners()
    this.startPeriodicSync()
  }

  // 设置网络状态监听
  private setupNetworkListeners(): void {
    window.addEventListener('online', () => {
      this.syncStatus.isOnline = true
      this.notifyListeners()
      this.syncPendingData()
    })

    window.addEventListener('offline', () => {
      this.syncStatus.isOnline = false
      this.notifyListeners()
    })
  }

  // 开始定期同步
  private startPeriodicSync(): void {
    // 每5分钟检查一次同步
    setInterval(() => {
      if (this.syncStatus.isOnline && !this.syncStatus.isSyncing) {
        this.syncPendingData()
      }
    }, 5 * 60 * 1000)
  }

  // 同步待处理数据
  async syncPendingData(): Promise<void> {
    if (this.syncStatus.isSyncing || !this.syncStatus.isOnline) {
      return
    }

    this.syncStatus.isSyncing = true
    this.syncStatus.pendingSync = 0
    this.notifyListeners()

    try {
      // 这里可以实现具体的同步逻辑
      // 例如：同步用户操作、表单提交等
      await this.syncUserActions()
      await this.syncFormSubmissions()
      
      this.syncStatus.lastSyncTime = new Date()
    } catch (error) {
      console.error('Sync failed:', error)
    } finally {
      this.syncStatus.isSyncing = false
      this.notifyListeners()
    }
  }

  // 同步用户操作
  private async syncUserActions(): Promise<void> {
    // 实现用户操作的同步逻辑
    console.log('Syncing user actions...')
  }

  // 同步表单提交
  private async syncFormSubmissions(): Promise<void> {
    // 实现表单提交的同步逻辑
    console.log('Syncing form submissions...')
  }

  // 预加载关键数据
  async preloadCriticalData(): Promise<void> {
    if (!this.syncStatus.isOnline) {
      return
    }

    try {
      // 预加载新闻数据
      await this.preloadNews()
      // 预加载产品数据
      await this.preloadProducts()
      // 预加载解决方案数据
      await this.preloadSolutions()
      // 预加载FAQ数据
      await this.preloadFAQs()
    } catch (error) {
      console.error('Failed to preload critical data:', error)
    }
  }

  // 预加载新闻数据
  private async preloadNews(): Promise<void> {
    try {
      const response = await apiService.get('/news?limit=10')
      if (response.data) {
        await offlineStorageService.storeBatchData('news', response.data.map((item: any) => ({
          id: item._id || item.id,
          data: item
        })))
      }
    } catch (error) {
      console.error('Failed to preload news:', error)
    }
  }

  // 预加载产品数据
  private async preloadProducts(): Promise<void> {
    try {
      const response = await apiService.get('/products')
      if (response.data) {
        await offlineStorageService.storeBatchData('products', response.data.map((item: any) => ({
          id: item.id || item.slug,
          data: item
        })))
      }
    } catch (error) {
      console.error('Failed to preload products:', error)
    }
  }

  // 预加载解决方案数据
  private async preloadSolutions(): Promise<void> {
    try {
      const response = await apiService.get('/solutions')
      if (response.data) {
        await offlineStorageService.storeBatchData('solutions', response.data.map((item: any) => ({
          id: item.id,
          data: item
        })))
      }
    } catch (error) {
      console.error('Failed to preload solutions:', error)
    }
  }

  // 预加载FAQ数据
  private async preloadFAQs(): Promise<void> {
    try {
      const response = await apiService.get('/faq')
      if (response.data) {
        await offlineStorageService.storeBatchData('faq', response.data.map((item: any) => ({
          id: item.id,
          data: item
        })))
      }
    } catch (error) {
      console.error('Failed to preload FAQs:', error)
    }
  }

  // 获取离线数据
  getOfflineData<T>(type: 'news' | 'products' | 'solutions' | 'faq', id?: string): T[] | T | null {
    if (id) {
      const data = offlineStorageService.getDataById(type, id)
      return data ? data.data : null
    }
    
    const dataList = offlineStorageService.getDataByType(type)
    return dataList.map(item => item.data) as T[]
  }

  // 添加状态监听器
  addStatusListener(listener: (status: SyncStatus) => void): () => void {
    this.listeners.push(listener)
    return () => {
      const index = this.listeners.indexOf(listener)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  // 通知监听器
  private notifyListeners(): void {
    this.listeners.forEach(listener => listener({ ...this.syncStatus }))
  }

  // 获取当前状态
  getStatus(): SyncStatus {
    return { ...this.syncStatus }
  }

  // 手动触发同步
  async forceSync(): Promise<void> {
    await this.syncPendingData()
  }

  // 清理过期数据
  cleanExpiredData(): void {
    offlineStorageService.cleanExpiredData()
  }
}

export const offlineSyncService = new OfflineSyncService()
export default offlineSyncService